import type {ReifiedGeometryAttributeData, AbstractGeometryData, ReifiedGeometryData, ReifiedGeometryAttributeSetData} from "#template/interfaces/geometry-data"
import type {ReplaceLeafNodeTypes, IndexingDim, ValueType} from "#template/geometry-processing/geometry-graph"
import {AttributeRef, ExprTreeToken, attrTreeToListExpr, indexingDim, valueType} from "#template/geometry-processing/geometry-graph"
import {hashObject} from "@cm/utils"
import type {MeshNodes} from "@cm/render-nodes"
import type {GeometryAttributeSetData, GeometryAttributeRefData} from "#template/nodes/geometry-graph/interfaces"
import type {
    GeometryAttributeSetDataToGeometryAttributeRefDataSet,
    AttributeRefSet,
    GraphType,
} from "#template/nodes/geometry-graph/common/geometry-graph-node-declaration"
import {
    geometryAttributeSetDataToGeometryAttributeRefDataSet,
    collectDisplayGraphResourcesFromGeometryAttributeRefDataSet,
    attributeRefSetFromGeometryAttributeRefDataSet,
} from "#template/nodes/geometry-graph/common/geometry-graph-node-declaration"
import {z} from "zod/v4-mini"

export type ExprsTreeTokenSet = {[key: string]: ExprTreeToken | ExprsTreeTokenSet}

export type AttributeSetDataToExprTreeTokenSet<T extends IndexingDim, U extends GeometryAttributeSetData<T>> = ReplaceLeafNodeTypes<
    GeometryAttributeSetDataToGeometryAttributeRefDataSet<T, U>,
    GeometryAttributeRefData,
    ExprTreeToken
>

export type AttributeSetDataToAttributeRefSet<T extends IndexingDim, U extends GeometryAttributeSetData<T>> = ReplaceLeafNodeTypes<
    GeometryAttributeSetDataToGeometryAttributeRefDataSet<T, U>,
    GeometryAttributeRefData,
    AttributeRef
>

export function setupGeometryGenerationData<T extends IndexingDim, U extends GeometryAttributeSetData<T>>(
    attributeSetData: U,
): {
    input: AbstractGeometryData
    context: {renderExprsTreeTokenSet: AttributeSetDataToExprTreeTokenSet<T, U>; displayExprsTreeTokenSet: AttributeSetDataToExprTreeTokenSet<T, U>}
} {
    type AttrRefDataSet = GeometryAttributeSetDataToGeometryAttributeRefDataSet<T, U>
    type AttrRefSet = AttributeSetDataToAttributeRefSet<T, U>
    type Tokens = AttributeSetDataToExprTreeTokenSet<T, U>

    const attributeRefDataSet: AttrRefDataSet = geometryAttributeSetDataToGeometryAttributeRefDataSet(attributeSetData)
    const displayGraphResources = collectDisplayGraphResourcesFromGeometryAttributeRefDataSet(attributeRefDataSet)

    const geomGraphDataFromAttributeRefDataSet = (
        attributeRefDataSet: AttrRefDataSet,
        graphType: GraphType,
    ): {geomGraph: MeshNodes.GeomGraph; tokens: Tokens} => {
        const attributeRefSet: AttrRefSet = attributeRefSetFromGeometryAttributeRefDataSet(attributeRefDataSet, graphType)

        let primaryAttributeRef: AttributeRef | null = null
        const traverse = <T extends AttributeRef | AttributeRefSet>(data: T) => {
            if (data instanceof AttributeRef) {
                if (primaryAttributeRef === null) primaryAttributeRef = data
            } else if (typeof data === "object") Object.keys(data).forEach((key) => traverse(data[key]))
            else throw new Error(`Invalid geometry attribute data`)
        }

        traverse(attributeRefSet)
        if (primaryAttributeRef === null) {
            throw new Error(`No primary attribute reference found in geometry attribute set data`)
        }

        const [ctx, tokens, exprList] = attrTreeToListExpr(primaryAttributeRef, attributeRefSet)

        const geomGraph: MeshNodes.GeomGraph = {
            type: "geomGraph",
            graph: exprList,
        }

        return {geomGraph, tokens: tokens as Tokens}
    }

    const {geomGraph: renderGeomGraph, tokens: renderExprsTreeTokenSet} = geomGraphDataFromAttributeRefDataSet(attributeRefDataSet, "render")
    const {geomGraph: displayGeomGraph, tokens: displayExprsTreeTokenSet} = geomGraphDataFromAttributeRefDataSet(attributeRefDataSet, "display")

    return {
        input: {
            contentHash: hashObject([renderGeomGraph, displayGeomGraph]),
            displayGeometryGraph: displayGeomGraph,
            renderGeometryGraph: renderGeomGraph,
            displayGeometryGraphResources: displayGraphResources,
        },
        context: {
            renderExprsTreeTokenSet,
            displayExprsTreeTokenSet,
        },
    }
}

export function reifiedGeometryDataToReifiedGeometryAttributeSetData<T extends ExprsTreeTokenSet>(
    reifiedGeometryData: ReifiedGeometryData,
    exprsTreeTokenSet: T,
    expectedIndexingDim: IndexingDim,
): ReplaceLeafNodeTypes<T, ExprTreeToken, ReifiedGeometryAttributeData> {
    const getReifiedAttributeDataForToken = (token: ExprTreeToken): ReifiedGeometryAttributeData => {
        if (token.index < 0 || token.index >= reifiedGeometryData.attributes.length) {
            throw new Error(`Invalid token index ${token.index} for reified geometry data with ${reifiedGeometryData.attributes.length} attributes`)
        }
        return reifiedGeometryData.attributes[token.index]
    }

    const validateAttributeData = (reifiedAttributeData: ReifiedGeometryAttributeData, token: ExprTreeToken) => {
        if (reifiedAttributeData.indexingDim !== expectedIndexingDim) {
            throw new Error(`
                Invalid reified geometry attribute data, expected indexing dimension ${expectedIndexingDim}, got ${reifiedAttributeData.indexingDim}`)
        }
        if (reifiedAttributeData.valueType !== token.type) {
            throw new Error(`
                Invalid reified geometry attribute data, expected value type ${token.type}, got ${reifiedAttributeData.valueType}`)
        }
    }

    const traverse = <T extends ExprTreeToken | ExprsTreeTokenSet>(node: T) => {
        if (node instanceof ExprTreeToken) {
            const reifiedAttributeData = getReifiedAttributeDataForToken(node)
            validateAttributeData(reifiedAttributeData, node)
            return reifiedAttributeData
        } else if (typeof node === "object") {
            const reifiedAttributeSetData: ReifiedGeometryAttributeSetData = {}
            for (const [key, value] of Object.entries(node)) {
                reifiedAttributeSetData[key] = traverse(value)
            }
            return reifiedAttributeSetData
        } else {
            throw new Error(`Invalid node type in expression tree: ${typeof node}`)
        }
    }

    return traverse(exprsTreeTokenSet) as ReplaceLeafNodeTypes<T, ExprTreeToken, ReifiedGeometryAttributeData>
}

export type ArrayForAttributeValueType<T extends ValueType> = T extends "uint32" ? Uint32Array : Float32Array

export const reifiedGeometryAttributeData = z.object({
    data: z.union([z.instanceof(Float32Array), z.instanceof(Uint32Array)]),
    valueType,
    indexingDim,
})

export function isReifiedGeometryAttributeData(data: ReifiedGeometryAttributeData | ReifiedGeometryAttributeSetData): data is ReifiedGeometryAttributeData {
    return reifiedGeometryAttributeData.safeParse(data).success
}

export function isReifiedGeometryAttributeSetData(
    data: ReifiedGeometryAttributeData | ReifiedGeometryAttributeSetData,
): data is ReifiedGeometryAttributeSetData {
    return !isReifiedGeometryAttributeData(data)
}

export function isExpectedReifiedGeometryAttributeData<T extends IndexingDim, U extends ValueType>(
    reifiedGeometryData: ReifiedGeometryAttributeData | ReifiedGeometryAttributeSetData,
    expectedIndexingDim: T,
    expectedValueType: U,
): reifiedGeometryData is {data: ArrayForAttributeValueType<U>; valueType: U; indexingDim: T} {
    const parsedAttributeData = reifiedGeometryAttributeData.safeParse(reifiedGeometryData)

    if (!parsedAttributeData.success) throw new Error(`Invalid reified geometry attribute data: ${JSON.stringify(reifiedGeometryData)}`)
    if (parsedAttributeData.data.indexingDim !== expectedIndexingDim)
        throw new Error(`Invalid reified geometry attribute data indexing dim: ${parsedAttributeData.data.indexingDim}, expected ${expectedIndexingDim}`)
    if (parsedAttributeData.data.valueType !== expectedValueType)
        throw new Error(`Invalid reified geometry attribute data value type: ${parsedAttributeData.data.valueType}, expected ${expectedValueType}`)
    if (expectedValueType === "uint32" && !(parsedAttributeData.data.data instanceof Uint32Array))
        throw new Error(`Invalid reified geometry attribute data type: ${typeof parsedAttributeData.data.data}, expected Uint32Array`)
    if (expectedValueType === "float" && !(parsedAttributeData.data.data instanceof Float32Array))
        throw new Error(`Invalid reified geometry attribute data type: ${typeof parsedAttributeData.data.data}, expected Float32Array`)

    return true
}
